/**
 * Claro e escuro — pedido da equipe, e um dos poucos itens do sistema que a
 * pessoa mexe todo dia.
 *
 * Três estados, não dois: "sistema" é o padrão e não estampa nada no <html>,
 * deixando o `prefers-color-scheme` mandar; "claro" e "escuro" estampam
 * `data-tema` e ganham do sistema operacional. Os tokens em `tokens.css` estão
 * escritos para os três.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"

export type Tema = "sistema" | "claro" | "escuro"

const CHAVE = "sistema-oc.tema"

type Ctx = { tema: Tema; definir: (t: Tema) => void; alternar: () => void; escuroAgora: boolean }
const Contexto = createContext<Ctx | null>(null)

function lerGravado(): Tema {
  try {
    const t = localStorage.getItem(CHAVE)
    return t === "claro" || t === "escuro" ? t : "sistema"
  } catch {
    return "sistema"
  }
}

function soPrefereEscuro(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches
}

export function ProvedorDeTema({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(lerGravado)
  const [soEscuro, setSoEscuro] = useState(soPrefereEscuro)

  useEffect(() => {
    if (typeof matchMedia !== "function") return
    const mq = matchMedia("(prefers-color-scheme: dark)")
    const ouvir = (e: MediaQueryListEvent) => setSoEscuro(e.matches)
    mq.addEventListener("change", ouvir)
    return () => mq.removeEventListener("change", ouvir)
  }, [])

  useEffect(() => {
    const raiz = document.documentElement
    if (tema === "sistema") raiz.removeAttribute("data-tema")
    else raiz.setAttribute("data-tema", tema)
    try { localStorage.setItem(CHAVE, tema) } catch { /* sem armazenamento */ }
  }, [tema])

  const escuroAgora = tema === "escuro" || (tema === "sistema" && soEscuro)

  const definir = useCallback((t: Tema) => setTema(t), [])
  const alternar = useCallback(() => setTema(escuroAgora ? "claro" : "escuro"), [escuroAgora])

  const valor = useMemo(() => ({ tema, definir, alternar, escuroAgora }), [tema, definir, alternar, escuroAgora])
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function usarTema(): Ctx {
  const c = useContext(Contexto)
  if (!c) throw new Error("usarTema() fora do ProvedorDeTema")
  return c
}
